/**
 * =========================================================================================
 * Pricing Plan - Plan Grid (Step 1: Choose Plan)
 * =========================================================================================
 */

import React from 'react';
import * as Icon from 'ubs-icons';
import PlanCard from './plan-card';
import { BILLING_CYCLE, BILLING_CYCLE_OPTIONS, PLAN_CURRENCY, SUBSCRIPTION_STATUS } from '../../../../../../common/constant';
import { useSelector } from 'react-redux';
import { getDiscountForCycle } from './utils';
import UAlertMessage from 'ubs-framework/v2/UAlertMessage';
import { COLOR } from 'ubs-framework/v2/common/constant';

const PlanGrid = React.memo(({ plans, dataLoaded, selectedPlanId, onSelectPlan, onContinue, currency, onCurrencyChange, subscriptionStatus, onResumeSubscription, billingCycle, onBillingCycleChange }) => {
    const _PI = useSelector(e => e?.common?.planInfo);

    const isTrialExpired = _PI?.isTrialAccount && _PI?.end && new Date(_PI?.end) < new Date(new Date().setHours(0, 0, 0, 0));

    const hasAnyDiscount = plans?.some(plan => {
        const disc = getDiscountForCycle(plan?.discount, billingCycle);
        return disc > 0;
    });

    return (
    <>
        {isTrialExpired && (
            <div className="u_pricing-plan__alert u_mb-20">
                <UAlertMessage danger full>
                    <div className="u_flex-align">
                        <Icon.Warning size={16} fill={COLOR.u_red} className="u_mr-10 u_flex-shrink" />
                        <div>
                            <span className="u_small-font u_fw-700">Your free trial has ended.</span>
                            <span className="u_small-font u_ml-4">Choose a plan below and add your billing details to continue using all features.</span>
                        </div>
                    </div>
                </UAlertMessage>
            </div>
        )}
        <div className="u_pricing-plan__header u_text-center">
            <div className="u_flex u_content-center u_mb-16">
                <div className="u_pricing-plan__card-badge u_color-blue">Simple, transparent pricing</div>
            </div>
            <h2 className="u_pricing-plan__title">Choose the perfect plan</h2>
            <p className="u_pricing-plan__subtitle">
                Go through the rich features your business would require and choose the plan that's just right for you and your team.
            </p>

            {/* Billing Cycle Selector */}
            {!_PI?.subscription && <div className="u_flex-align u_content-center u_mt-24">
                <div className="u_flex-align u_bg-thead u_p-8 u_billing">
                    {BILLING_CYCLE_OPTIONS.map((opt) => {
                        const isActive = billingCycle === opt.value;
                        const maxDiscount = Math.max(...(plans || []).map(p => getDiscountForCycle(p?.discount, opt.value)), 0);
                        return (
                            <div
                                key={opt.value}
                                className={`u_billing-tab u_py-6 u_px-10 u_cp u_text-center u_small-font u_fw-600 u_flex-align ${isActive ? 'u_bg-blue u_color-white' : 'u_color-title'}`}
                                onClick={() => onBillingCycleChange(opt.value)}
                            >
                                <span>{opt.label}</span>
                                {maxDiscount > 0 && (
                                    <span className={`u_pricing-plan__card-badge green u_color-green u_ml-6`}>
                                        Save {maxDiscount}%
                                    </span>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>}
        </div>

        {dataLoaded ? (
            <div className="u_pricing-plan__grid u_row u_content-center">
                {plans?.map(plan => (
                    <PlanCard
                        key={plan._id}
                        plan={plan}
                        selectedPlanId={selectedPlanId}
                        onSelect={onSelectPlan}
                        onContinue={onContinue}
                        currency={currency}
                        subscriptionStatus={subscriptionStatus}
                        onResumeSubscription={onResumeSubscription}
                        billingCycle={billingCycle}
                    />
                ))}
            </div>
        ) : (
            <div className="u_pricing-plan__loader u_flex u_flex-col u_align-center u_content-center">
                <Icon.Preloader size={50} />
                <span className="u_small-font">Loading plans...</span>
            </div>
        )}
    </>
    )}
);

export default PlanGrid;
